import { useEffect, useState } from "react";

/**
 * Persisted audio settings — volume (0–1) and muted (bool).
 *
 * Written to localStorage so the pilot's choice survives app restarts.
 * Also broadcasts changes via a CustomEvent so multiple mounted consumers
 * (e.g. the volume dropdown in the sidebar and the <audio> element in the
 * SoundPlayer) stay in sync without a shared React context.
 */
const KEY_VOL   = "vacars.audio.volume";
const KEY_MUTED = "vacars.audio.muted";
const EVT       = "vacars-audio-settings";

const DEFAULT_VOL = 0.8;

function readVol() {
  // Check for "nothing stored" BEFORE Number(). `Number(null)` is 0, and 0
  // passes the range guard below, so this function could never return its own
  // default: a pilot with no saved preference got volume 0 and a silent app,
  // with both sliders reading 0%.
  //
  // An explicitly stored 0 is a different thing and must survive — a pilot who
  // drags the slider to zero means it. Same distinction as a pax count of 0 on
  // a ferry leg versus a pax count nobody recorded.
  const raw = typeof window === "undefined" ? null : window.localStorage?.getItem(KEY_VOL);
  if (raw === null || raw === undefined || raw === "") return DEFAULT_VOL;
  const n = Number(raw);
  return Number.isFinite(n) && n >= 0 && n <= 1 ? n : DEFAULT_VOL;
}
function readMuted() {
  const raw = typeof window !== "undefined" && window.localStorage?.getItem(KEY_MUTED);
  return raw === "1";
}

export function getAudioSettings() {
  return { volume: readVol(), muted: readMuted() };
}

export function setAudioSettings({ volume, muted }) {
  if (typeof window === "undefined") return;
  if (volume !== undefined) window.localStorage.setItem(KEY_VOL, String(volume));
  if (muted  !== undefined) window.localStorage.setItem(KEY_MUTED, muted ? "1" : "0");
  window.dispatchEvent(new CustomEvent(EVT, { detail: getAudioSettings() }));
}

/** React hook — returns current settings + a setter, and re-renders on any change. */
export function useAudioSettings() {
  const [s, setS] = useState(getAudioSettings);
  useEffect(() => {
    const handler = (e) => setS(e.detail || getAudioSettings());
    window.addEventListener(EVT, handler);
    // Also react to changes made in another tab (rare in Electron but cheap).
    const storage = (e) => {
      if (e.key === KEY_VOL || e.key === KEY_MUTED) setS(getAudioSettings());
    };
    window.addEventListener("storage", storage);
    return () => {
      window.removeEventListener(EVT, handler);
      window.removeEventListener("storage", storage);
    };
  }, []);
  return [s, setAudioSettings];
}
